/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import Button from 'components/common/Button';
import { deleteImage, uploadPhotos, onCompleteUploadImage, updatePreviewImages } from 'actions/scene';
import * as photoActions from 'actions/photos';
import { useSelector, batch } from 'react-redux';
import useIsMobile from 'hooks/useIsMobile';
import Grid from 'components/common/NewGrid';
import classnames from 'classnames';
import ReactFileReader from 'react-file-reader';
import { last, chunk } from 'lodash';
import { v4 as uuid } from 'uuid';
import ImageUploader from 'utils/image-upload/adapters/pixaprints';
import { useActivePhotoId } from 'hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faHandPointUp } from '@fortawesome/pro-regular-svg-icons';
import store from 'store';
import GalleryExplorer from 'utils/gallery-explorer';
import { modalTypes, modes } from 'constants/index';
import Loading from 'components/layout/MainLayout/Loading';
import MozillaAndroidDoesNotSupportMultiFileSelect from 'constants/mozillaAndroidDoesNotSupportMultiFileSelect';
import SceneHelper from 'utils/SceneHelper';
import { openModal } from 'utils/utils';
import { generateImagePreview, idMap } from './utils';
import './PhotoLibrary.scss';
import Switch from '../Switch';
import BackButton from '../GridBackButton';
import Photo from './components/Photo';
import Folder from './components/Folder';

const MemoReactFileReader = React.memo(ReactFileReader);

const selectedOnboardingImage = { selected: false };

const uploader = new ImageUploader();
const explorer = new GalleryExplorer();
export const uploadAndWriteToStore = async (files, onClick) => {
  const folderId = explorer.getCurrentFolder();
  if (!selectedOnboardingImage.selected) selectedOnboardingImage.selected = true;
  const filesChunks = chunk(files, 50);
  const mode = SceneHelper.getMode(store.getState());
  const canvas = document.createElement(modes.canvas);
  const context = canvas.getContext('2d');

  for (const files of filesChunks) {
    const photosWithoutPreview = [];
    for (const file of files) {
      file.id = file.id || uuid();
      file.folderId = folderId;
      const photo = await generateImagePreview(file, { doNotCreateThumbnail: true });
      photosWithoutPreview.push(photo);
    }
    batch(() => {
      store.dispatch(photoActions.addToStart(photosWithoutPreview));
      if (onClick) {
        onClick(photosWithoutPreview);
      }
      if (mode === modes.photoPrint) {
        store.dispatch(uploadPhotos({ images: photosWithoutPreview }));
      }
    });
    for (const file of files) {
      uploader.enqueue(file, {
        onProgress: ({ progress }) => {
          store.dispatch(photoActions.updateUploadProgress({ id: file.id, progress }));
        },
        onComplete: ({ url, data }) => {
          const { naturalHeight, naturalWidth, width, height, thumbnailSrc, ...photo } = explorer.normalizePhoto(data);
          store.dispatch(
            onCompleteUploadImage({
              image: {
                oldId: file.id,
                id: photo.id,
                thumbnailSrc,
                naturalHeight,
                naturalWidth,
                width,
                height,
                initialized: false,
                uploaded: true,
              },
            }),
          );
          store.dispatch(
            photoActions.uploadCompleted({
              id: file.id,
              photo: {
                ...photo,
                src: url,
                thumbnailSrc,
                rotate: naturalHeight > naturalWidth,
                naturalHeight,
                naturalWidth,
                width,
                height,
              },
            }),
          );
          idMap[file.id] = photo.id;
        },
        onError: ({ error }) => {
          console.log('error:', error);
          store.dispatch(photoActions.uploadError({ id: file.id, error }));
          store.dispatch(deleteImage({ id: file.id, error }));
          const message = 'Photo upload failed. Please try again';
          openModal({ type: modalTypes.warning, modalProps: { message } });
        },
      });
    }
  }

  const filesChunksForImageUpdate = chunk(files, 5);
  for (const files of filesChunksForImageUpdate) {
    const photosWithPreview = [];
    for (const file of files) photosWithPreview.push(await generateImagePreview(file, { canvas, context }));
    const newPreview = photosWithPreview.map(
      ({ id: _id, naturalWidth, naturalHeight, width, height, src, thumbnailSrc }) => ({
        id: _id,
        initialized: false,
        naturalWidth,
        naturalHeight,
        width,
        height,
        src,
        thumbnailSrc,
      }),
    );
    batch(() => {
      store.dispatch(photoActions.update({ addFields: photosWithPreview, updatePreview: true }));
      store.dispatch(updatePreviewImages({ images: newPreview }));
    });
  }
};

const fontAwesomeIconStyle = { marginRight: 10, height: '1.2em', width: '1.2em' };
const fontAwesomeIconButtonStyle = { borderColor: 'var(--toolbar-bg)', float: 'right', display: 'flex' };

const ClickImport = () => (
  <div className="no-photos">
    <FontAwesomeIcon.Memo icon={faHandPointUp} />
    <span>Click upload photo's to begin</span>
  </div>
);
const fileTypes = ['.png', '.jpg', '.jpeg', '.heic'];
const PhotoLibrary = ({ itemWidth, itemHeight, compact, alwaysShowWhiteRectangle, scrollToTop, maxLine }) => {
  const activePhotoIds = useActivePhotoId();
  const isLoading = useSelector(({ photos: { loading } }) => loading);
  const [hideUsedPhotos, setHideUsedPhotos] = useState(false);
  const toggleHideUsedPhotos = useCallback(() => setHideUsedPhotos(!hideUsedPhotos), [hideUsedPhotos]);
  const photos = useSelector(({ photos: { photos } }) => photos);
  const showPhotos = useSelector(({ photos: { showPhotos } }) => showPhotos);
  const folders = useSelector(({ photos: { folders } }) => folders);
  const foldersHistory = useSelector(({ photos: { foldersHistory } }) => foldersHistory);
  const currentFolder = last(foldersHistory);
  const handleFileUpload = React.useCallback((files) => {
    uploadAndWriteToStore(files);
  }, []);
  const fileReaderChildren = useMemo(
    () => (
      <Button
        type="button"
        size="lg"
        textSize="md"
        rounded
        style={fontAwesomeIconButtonStyle}
        color="black"
        className="button-upload-photo"
      >
        <FontAwesomeIcon.Memo icon={faUpload} style={fontAwesomeIconStyle} />
        <span>Upload photo's</span>
      </Button>
    ),
    [],
  );
  const isMobile = useIsMobile();

  const gridRef = useRef();
  const isDataGrid = useMemo(
    () => !folders?.length && !photos?.length && !foldersHistory?.length,
    [folders?.length, foldersHistory?.length, photos?.length],
  );
  useEffect(() => {
    if (typeof scrollToTop === 'function') scrollToTop();
    if (typeof gridRef.current?.resetScroll === 'function') gridRef.current.resetScroll();
  }, [currentFolder, scrollToTop]);
  return (
    <div className={classnames('photo-library', { compact })}>
      <div className={`buttons-photos ${isDataGrid ? 'flex-column' : ''}`}>
        <div className="buttons-container">
          <MemoReactFileReader
            multipleFiles={!MozillaAndroidDoesNotSupportMultiFileSelect}
            fileTypes={fileTypes}
            handleFiles={handleFileUpload}
          >
            {fileReaderChildren}
          </MemoReactFileReader>
        </div>
        {isDataGrid ? (
          <ClickImport />
        ) : (
          <Switch
            label="Hide used photos"
            checked={hideUsedPhotos}
            onChange={toggleHideUsedPhotos}
            className="hide-used-photos"
          />
        )}
      </div>
      <Loading loading={isLoading} className="photoLibrary">
        <Grid
          ref={gridRef}
          disableItemsTransition={isMobile ? photos.length > 10 : photos.length > 20}
          width={itemWidth}
          height={itemHeight}
        >
          {foldersHistory?.length ? <BackButton onClick={() => explorer.goBack()} /> : null}
          {folders?.map((folder) => (
            <Folder key={folder.id} scrollToTop={scrollToTop} maxLine={maxLine} explorer={explorer} {...folder} />
          ))}
          {showPhotos?.map((photo) =>
            hideUsedPhotos && activePhotoIds.includes(photo.id) ? null : (
              <Photo
                key={photo.id}
                alwaysShowWhiteRectangle={alwaysShowWhiteRectangle}
                isActive={activePhotoIds.includes(photo.id)}
                photo={photo}
                {...photo}
              />
            ),
          )}
        </Grid>
      </Loading>
    </div>
  );
};

export default PhotoLibrary;
